import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchReport } from "@/lib/api";
import { Header, Footer } from "@/components/Brand";
import {
    Loader2,
    AlertCircle,
    CheckCircle2,
    Sparkles,
    Printer,
    Lightbulb,
    Heart,
    Target,
} from "lucide-react";

const PART_LABEL = {
    stop_hiding: "Part 1 — Stop Hiding",
    start_knowing: "Part 2 — Start Knowing",
    take_control: "Part 3 — Take Control",
    own_your_future: "Part 4 — Own Your Future",
};

export default function Report() {
    const { id } = useParams();
    const [report, setReport] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchReport(id)
            .then(setReport)
            .catch((e) => setError(e?.response?.data?.detail || "Report not found"));
    }, [id]);

    if (error) {
        return (
            <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6">
                <AlertCircle className="w-8 h-8 text-black mb-3" />
                <h2 className="font-display uppercase text-3xl text-black">{error}</h2>
                <Link to="/assessment" className="mt-6 underline text-black font-bold">Take the Reality Check</Link>
            </div>
        );
    }
    if (!report) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-black animate-spin" />
            </div>
        );
    }

    const {
        name,
        band,
        biggest_gap,
        strongest_part,
        parts,
        narrative,
        primary_next_step,
        companion_next_step,
        secondary_next_steps,
        free_chat,
    } = report;

    return (
        <div className="min-h-screen bg-white" data-testid="report-page">
            <div className="no-print">
                <Header />
            </div>

            <main className="max-w-5xl mx-auto px-6 sm:px-10 pt-6 pb-20">

                {/* HERO — Name + tagline */}
                <div className="fade-up text-center max-w-4xl mx-auto">
                    <p className="overline mb-3 text-pm-yellow-deep">Your Profitable-But-Broke Reality Check</p>
                    <h1 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl text-black leading-[0.95]" data-testid="report-hero">
                        Hi {name}, here&apos;s your <span className="bg-pm-yellow px-2 inline-block">Reality Check.</span>
                    </h1>
                    <p className="mt-6 text-lg text-black/75 leading-relaxed max-w-2xl mx-auto">
                        The honest read on where you sit right now — and the piece of the
                        <strong> Follow The Cash™</strong> method you need most.
                    </p>
                </div>

                {/* DIAGNOSIS card — band name is the hero, no raw score */}
                <div
                    className="mt-12 border-2 border-black rounded-[22px] p-10 sm:p-14 fade-up-d1 relative overflow-hidden"
                    style={{ background: "#FFBD4A", boxShadow: "10px 10px 0 #000" }}
                    data-testid="diagnosis-card"
                >
                    <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-black/5" aria-hidden />
                    <p className="overline mb-3">Your Reality Check diagnosis</p>
                    <h2
                        className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl text-black leading-[0.9]"
                        data-testid="band-name"
                    >
                        {band.name}
                    </h2>
                    <p className="mt-5 italic text-black/85 text-lg leading-relaxed" data-testid="band-tagline">
                        {band.tagline}
                    </p>
                    <p className="mt-6 text-black/90 text-base sm:text-lg leading-relaxed max-w-3xl" data-testid="band-blurb">
                        {band.blurb}
                    </p>

                    <div className="mt-8 pt-6 border-t-2 border-black/20 grid sm:grid-cols-2 gap-4">
                        <div className="flex items-start gap-3" data-testid="biggest-gap-callout">
                            <Target className="w-5 h-5 mt-0.5 flex-shrink-0 text-black" />
                            <div>
                                <p className="overline mb-1">Your biggest gap</p>
                                <p className="font-display uppercase text-lg text-black leading-tight">
                                    {PART_LABEL[biggest_gap.id] || biggest_gap.name}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3" data-testid="strongest-part-callout">
                            <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0 text-black" />
                            <div>
                                <p className="overline mb-1">Your strongest area</p>
                                <p className="font-display uppercase text-lg text-black leading-tight">
                                    {PART_LABEL[strongest_part.id] || strongest_part.name}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* AI Headline + Summary */}
                <div className="mt-10 bg-black text-white rounded-3xl p-10 sm:p-12 fade-up-d2 relative overflow-hidden" data-testid="narrative-card">
                    <div className="absolute -top-8 -right-8 w-44 h-44 rounded-full bg-pm-yellow/20" aria-hidden />
                    <Sparkles className="w-5 h-5 text-pm-yellow mb-3 relative" />
                    <h2 className="font-display uppercase text-3xl sm:text-5xl text-white leading-[0.95] relative" data-testid="narrative-headline">
                        {narrative.headline}
                    </h2>
                    <p className="mt-5 text-white/85 text-lg leading-relaxed max-w-3xl relative" data-testid="narrative-summary">
                        {narrative.summary}
                    </p>
                    <p className="mt-5 text-white/80 text-base leading-relaxed max-w-3xl relative" data-testid="narrative-this-means">
                        <span className="font-bold uppercase tracking-widest text-pm-yellow text-xs mr-2">What this means:</span>
                        {narrative.this_means}
                    </p>
                </div>

                {/* Per-Part breakdown — 4 cards */}
                <SectionDivider num="01" title="Your four parts" />
                <p className="text-black/75 text-base sm:text-lg mb-6 leading-relaxed">
                    The four parts of Follow The Cash™. Higher bar = more clarity. Your biggest gap is highlighted.
                </p>
                <div className="grid sm:grid-cols-2 gap-4" data-testid="parts-grid">
                    {parts.map((p) => {
                        const isGap = p.id === biggest_gap.id;
                        return (
                            <div
                                key={p.id}
                                className={`rounded-2xl p-6 border-2 ${isGap ? "border-pm-yellow-deep bg-pm-yellow/15" : "border-black bg-white"}`}
                                data-testid={`part-${p.id}`}
                            >
                                <div className="flex items-baseline justify-between">
                                    <p className="overline">Part {p.part_number}</p>
                                    <span className="rounded-full px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] bg-black text-pm-yellow font-bold">
                                        {p.status}
                                    </span>
                                </div>
                                <p className="font-display uppercase text-xl text-black leading-tight mt-2">{p.name}</p>
                                <p className="text-sm text-black/65 mt-1 italic leading-snug">{p.tagline}</p>
                                <div className="mt-4 flex items-baseline gap-2">
                                    <span className="font-display text-5xl text-black">{p.clarity_pct}<span className="text-black/40 text-lg">%</span></span>
                                    <span className="text-xs uppercase tracking-widest text-black/50 ml-2">clarity</span>
                                </div>
                                <div className="mt-3 h-2 bg-black/10 rounded-full overflow-hidden">
                                    <div className="h-full bg-pm-yellow transition-all" style={{ width: `${p.clarity_pct}%` }} />
                                </div>
                                {isGap && (
                                    <p className="mt-3 text-xs uppercase tracking-widest text-pm-yellow-deep font-bold flex items-center gap-1.5">
                                        <Target className="w-3 h-3" /> Your biggest gap
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* SECTION — Three things to do this week */}
                <SectionDivider num="02" title="Three things to do this week" />
                <p className="text-black/75 text-base sm:text-lg max-w-3xl mb-6 leading-relaxed">
                    Small, doable, no spend required. Start following the cash yourself — before any next step.
                </p>
                <div className="pm-card p-8 sm:p-10 bg-pm-yellow/15" data-testid="three-actions-card">
                    <ol className="space-y-5 list-none">
                        {(narrative.three_things_to_do_now || []).map((s, i) => (
                            <li key={i} className="flex items-start gap-5 text-black text-base sm:text-lg leading-relaxed">
                                <span className="font-display text-4xl text-pm-yellow-deep leading-none flex-shrink-0 w-12">0{i + 1}</span>
                                <span>{s}</span>
                            </li>
                        ))}
                    </ol>
                </div>

                {/* SECTION — What's working */}
                <SectionDivider num="03" title="What's working" />
                <div className="pm-card p-8 sm:p-10" data-testid="strengths-card">
                    <ul className="space-y-4">
                        {(narrative.whats_working || []).map((s, i) => (
                            <li key={i} className="flex items-start gap-4 text-black text-base sm:text-lg leading-relaxed">
                                <CheckCircle2 className="w-6 h-6 text-pm-yellow-deep flex-shrink-0 mt-1" />
                                <span>{s}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* SECTION — Where the gaps are */}
                <SectionDivider num="04" title="Where the gaps are" />
                <div className="pm-card p-8 sm:p-10 bg-pm-yellow" data-testid="gaps-card">
                    <ul className="space-y-4">
                        {(narrative.where_the_gaps_are || []).map((s, i) => (
                            <li key={i} className="flex items-start gap-4 text-black text-base sm:text-lg leading-relaxed">
                                <Lightbulb className="w-6 h-6 text-black flex-shrink-0 mt-1" />
                                <span>{s}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* SECTION — Recommended next step */}
                <SectionDivider num="05" title="Your next best step" />
                <div className="bg-black text-white rounded-3xl p-10 sm:p-14 relative overflow-hidden" data-testid="recommended-card">
                    <div className="absolute -bottom-12 -right-12 w-48 h-48 rounded-full bg-pm-yellow/20" aria-hidden />
                    <div className="relative max-w-3xl">
                        <p className="overline text-pm-yellow mb-4">Recommended for you</p>
                        <h2 className="font-display uppercase text-3xl sm:text-5xl text-white leading-[0.95]" data-testid="recommended-title">
                            {primary_next_step.title}
                        </h2>
                        <p className="mt-3 text-pm-yellow font-bold uppercase tracking-[0.18em] text-xs">
                            {primary_next_step.subtitle}
                        </p>
                        <p className="mt-6 text-lg leading-relaxed text-white/90" data-testid="why-this-fits">
                            {narrative.next_step_why_this_fits}
                        </p>
                        <p className="mt-5 text-white/80 leading-relaxed">
                            {primary_next_step.description}
                        </p>
                        <a
                            href={primary_next_step.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="cta-primary"
                            className="btn-pm mt-8 text-sm"
                        >
                            {primary_next_step.cta_label} <span>→</span>
                        </a>
                    </div>
                </div>

                {/* Companion step — for bands with a two-step recommendation */}
                {companion_next_step && (
                    <div
                        className="mt-6 border-2 border-black rounded-[18px] p-8 sm:p-10 bg-pm-yellow flex flex-col sm:flex-row sm:items-end gap-6"
                        data-testid="companion-card"
                        style={{ boxShadow: "6px 6px 0 #000" }}
                    >
                        <div className="flex-1">
                            <p className="overline mb-3">Paired with your recommended step</p>
                            <h3 className="font-display uppercase text-2xl sm:text-3xl text-black leading-[0.95]" data-testid="companion-title">
                                {companion_next_step.title}
                            </h3>
                            <p className="mt-3 text-black/80 leading-relaxed">
                                {companion_next_step.description}
                            </p>
                        </div>
                        <a
                            href={companion_next_step.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="cta-companion"
                            className="btn-pm-dark text-xs sm:self-end whitespace-nowrap"
                        >
                            {companion_next_step.cta_label} <span>→</span>
                        </a>
                    </div>
                )}

                {/* SECTION — Note from Magdalena */}
                <div className="mt-10 border-2 border-black rounded-3xl p-8 sm:p-12 bg-pm-yellow/15" data-testid="magdalena-note-card">
                    <p className="overline mb-4 text-pm-yellow-deep flex items-center gap-2"><Heart className="w-4 h-4" /> A note from Magdalena</p>
                    <p className="text-black/85 text-lg leading-relaxed italic">
                        {narrative.note_from_magdalena}
                    </p>
                </div>

                {/* SECTION — Other doorways */}
                <SectionDivider num="06" title="Or — if you'd prefer a different path" />
                <p className="text-black/75 text-base sm:text-lg max-w-3xl mb-8 leading-relaxed">
                    The step above is the closest fit for where you are right now. But these other doorways
                    are also open — pick what feels right.
                </p>

                <div className="grid gap-6" data-testid="secondary-steps-section">
                    {secondary_next_steps.map((step, i) => (
                        <div key={i} className="border-2 border-black rounded-[18px] p-8 sm:p-10 bg-white flex flex-col sm:flex-row sm:items-end gap-6" data-testid={`secondary-${i}`}>
                            <div className="flex-1">
                                <p className="overline mb-3">{step.subtitle}</p>
                                <h3 className="font-display uppercase text-2xl sm:text-3xl text-black leading-[0.95]">
                                    {step.title}
                                </h3>
                                <p className="mt-4 text-black/80 leading-relaxed">
                                    {step.description}
                                </p>
                            </div>
                            <a
                                href={step.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`cta-secondary-${i}`}
                                className="btn-pm text-xs sm:self-end whitespace-nowrap"
                            >
                                {step.cta_label} <span>→</span>
                            </a>
                        </div>
                    ))}
                </div>

                {/* Free chat — universal + PDF download */}
                <div className="no-print mt-10 bg-pm-yellow border-2 border-black rounded-2xl p-8 sm:p-12 relative overflow-hidden" data-testid="free-chat-card" style={{ boxShadow: "8px 8px 0 #000" }}>
                    <div className="absolute -bottom-12 -left-12 w-44 h-44 rounded-full bg-black/5" aria-hidden />
                    <div className="relative grid md:grid-cols-2 gap-6 items-center">
                        <div>
                            <p className="overline mb-3">{free_chat.subtitle}</p>
                            <h2 className="font-display uppercase text-3xl sm:text-4xl text-black leading-[0.95]">
                                Want to chat it through?
                            </h2>
                            <p className="mt-4 text-black/85 leading-relaxed">
                                {free_chat.description}
                            </p>
                        </div>
                        <div className="flex flex-col gap-3 md:items-end">
                            <a
                                href={free_chat.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="btn-book-call"
                                className="btn-pm-dark text-xs w-full md:w-auto"
                            >
                                {free_chat.cta_label} <span>→</span>
                            </a>
                            <a
                                href={`${process.env.REACT_APP_BACKEND_URL}/api/assessment/report/${id}/pdf`}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="btn-print"
                                className="btn-pm-secondary text-xs w-full md:w-auto"
                            >
                                <Printer className="w-4 h-4" /> Download PDF
                            </a>
                            <a
                                href="https://profitmind.co.uk"
                                data-testid="btn-home"
                                className="inline-flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-[0.08em] text-black/70 hover:text-black px-6 py-3 transition-colors w-full md:w-auto"
                            >
                                Visit profitmind.co.uk <span>→</span>
                            </a>
                        </div>
                    </div>
                </div>

            </main>

            <div className="no-print">
                <Footer />
            </div>
        </div>
    );
}

function SectionDivider({ num, title }) {
    return (
        <div className="my-12 flex items-end gap-5">
            <div className="flex-1">
                <p className="overline mb-2 text-pm-yellow-deep">Section {num}</p>
                <h2 className="font-display uppercase text-3xl sm:text-4xl text-black leading-none">{title}</h2>
            </div>
            <div className="flex-1 yellow-bar mb-2" />
        </div>
    );
}
